// Environment configuration with proper TypeScript types and fallbacks
declare global {
  interface Window {
    __REACT_APP_API_URL__?: string;
    __REACT_APP_AUTH_LOGIN_URL__?: string;
    __REACT_APP_AUTH_CLIENT_ID__?: string;
  }
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);

function urlHostIsLoopback(base: string): boolean {
  try {
    const u = new URL(base.includes('://') ? base : `http://${base}`);
    return LOOPBACK_HOSTS.has(u.hostname);
  } catch {
    return base.includes('localhost') || base.includes('127.0.0.1');
  }
}

/**
 * When the UI is served on a typical dev port, assume API is on the same host at :8000.
 * This fixes: open http://192.168.x.x:3000 while .env or Docker config still says http://localhost:8000
 * (the browser would otherwise call the wrong machine's localhost).
 */
function inferApiFromBrowserLocation(): string | null {
  if (typeof window === 'undefined') return null;
  const { protocol, hostname, port } = window.location;
  const p = port || (protocol === 'https:' ? '443' : '80');
  const devPorts = new Set(['3000', '5173']);
  if (!devPorts.has(p)) return null;
  const apiProtocol = protocol === 'https:' ? 'https:' : 'http:';
  return `${apiProtocol}//${hostname}:8000`;
}

/**
 * API origin for fetch (no trailing slash).
 * Order:
 * 1. If we're on a non-loopback host but runtime/env API URL points at localhost → use same-host :8000
 * 2. Runtime `/config.js` (Docker entrypoint)
 * 3. Build-time REACT_APP_API_URL
 * 4. Inferred same-host :8000 for dev ports
 * 5. CRA dev: '' → package.json "proxy"
 * 6. Fallback http://localhost:8000
 */
export const getApiUrl = (): string => {
  const inferred = inferApiFromBrowserLocation();
  const rawRuntime = typeof window !== 'undefined' ? window.__REACT_APP_API_URL__ : undefined;
  const runtime =
    rawRuntime != null && String(rawRuntime).trim() !== ''
      ? String(rawRuntime).trim().replace(/\/$/, '')
      : null;

  const rawEnv = process.env.REACT_APP_API_URL;
  const fromEnv =
    rawEnv != null && String(rawEnv).trim() !== '' ? String(rawEnv).trim().replace(/\/$/, '') : null;

  const pageHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const onLoopback = LOOPBACK_HOSTS.has(pageHost);

  if (inferred && !onLoopback) {
    const envSaysLoopback =
      (runtime != null && urlHostIsLoopback(runtime)) ||
      (fromEnv != null && urlHostIsLoopback(fromEnv));
    if (envSaysLoopback) {
      return inferred;
    }
  }

  if (runtime) return runtime;
  if (fromEnv) return fromEnv;
  if (inferred) return inferred;

  if (process.env.NODE_ENV === 'development') return '';
  return 'http://localhost:8000';
};

/** Base URL of the external auth service (UI). Unauthenticated users are redirected here with client_id and redirect_uri. */
export const getAuthLoginUrl = (): string | null => {
  const runtime = typeof window !== 'undefined' ? window.__REACT_APP_AUTH_LOGIN_URL__ : undefined;
  const url = (runtime && runtime.trim()) || process.env.REACT_APP_AUTH_LOGIN_URL || '';
  return url.trim() || null;
};

/** OAuth client_id for this app (e.g. allinfinance). Used when redirecting to auth and in callback. */
export const getAuthClientId = (): string => {
  const runtime = typeof window !== 'undefined' ? window.__REACT_APP_AUTH_CLIENT_ID__ : undefined;
  return (runtime && runtime.trim()) || (process.env.REACT_APP_AUTH_CLIENT_ID || 'allinfinance').trim();
};

/** URL where the external auth service should redirect after sign-in, with token in query (e.g. ?token=JWT). */
export const getAuthCallbackUrl = (): string => {
  if (typeof window === 'undefined') return '/auth/callback';
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${window.location.origin}${base}/auth/callback`;
};
